import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import ProductImageManager from './ProductImageManager';
import { secureUpload } from '../../../services/s3UploadService';

const {
  FiX, FiPlus, FiDollarSign, FiCheck, FiPackage, FiAlertTriangle,
  FiTruck, FiImage, FiChevronRight, FiChevronLeft, FiTrash2,
  FiLayers, FiTag, FiCamera, FiLoader, FiEdit2, FiSettings, FiUpload
} = FiIcons;

const PRODUCTS_TABLE   = 'merchant_products_1776540001';
const VARIATIONS_TABLE = 'product_variations_1776540010';

const STEPS = ['Details', 'Inventory', 'Variations', 'Images', 'Fulfillment'];

const VARIATION_TYPES = ['Color', 'Size', 'Material', 'Style', 'Weight', 'Custom'];
const DEFAULT_CATEGORIES = ['Flowers', 'Urns', 'Services', 'Accessories', 'Gifts', 'Candles'];

const FULFILLMENT_STATUSES = [
  { value: 'pending',    label: 'Pending',    color: 'bg-amber-50 text-amber-600 border-amber-200'    },
  { value: 'processing', label: 'Processing', color: 'bg-blue-50 text-blue-600 border-blue-200'       },
  { value: 'shipped',    label: 'Shipped',    color: 'bg-purple-50 text-purple-600 border-purple-200' },
  { value: 'delivered',  label: 'Delivered',  color: 'bg-green-50 text-green-600 border-green-200'    },
];

const emptyVariation = () => ({
  _key: crypto.randomUUID(),
  type: 'Color', name: '', value: '', price_modifier: 0, stock_count: 10, sku: '', is_active: 1,
});

const VariationRow = ({ variation, onChange, onRemove, index }) => {
  const set = (k, v) => onChange(variation._key, k, v);
  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-brand-light rounded-2xl border border-brand-dark p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Variation #{index + 1}</span>
        <button type="button" onClick={() => onRemove(variation._key)} className="p-1 text-text-muted hover:text-red-500 transition-colors">
          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Type</label>
          <select value={variation.type} onChange={e => set('type', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-brand-dark rounded-xl text-sm focus:border-accent-gold outline-none">
            {VARIATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Value</label>
          <input value={variation.value} onChange={e => set('value', e.target.value)}
            placeholder={variation.type === 'Color' ? 'e.g. Red' : variation.type === 'Size' ? 'e.g. Large' : 'e.g. Custom'}
            className="w-full px-3 py-2 bg-white border border-brand-dark rounded-xl text-sm focus:border-accent-gold outline-none" />
        </div>
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Price Modifier ($)</label>
          <div className="relative">
            <SafeIcon icon={FiDollarSign} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted w-3 h-3" />
            <input type="number" step="0.01" value={variation.price_modifier}
              onChange={e => set('price_modifier', parseFloat(e.target.value) || 0)}
              className="w-full pl-7 pr-3 py-2 bg-white border border-brand-dark rounded-xl text-sm focus:border-accent-gold outline-none" />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Stock Count</label>
          <input type="number" min="0" value={variation.stock_count}
            onChange={e => set('stock_count', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-white border border-brand-dark rounded-xl text-sm focus:border-accent-gold outline-none" />
        </div>
      </div>
    </motion.div>
  );
};

const AddProductModal = ({ isOpen, onClose, onSuccess, editData }) => {
  const [step,          setStep]          = useState(0);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [createdId,     setCreatedId]     = useState(null); 
  
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('merchant_product_categories');
      return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
    } catch (e) {
      return DEFAULT_CATEGORIES;
    }
  });

  const [formData, setFormData] = useState({
    name: '', price: '', category: categories[0] || 'Flowers', description: '',
    image_url: 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?q=80&w=1287&auto=format&fit=crop',
    stock_count: 0, low_stock_threshold: 5, track_inventory: true, fulfillment_status: 'pending',
  });
  
  const [variations, setVariations] = useState([]);
  
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const [isManagingCat, setIsManagingCat] = useState(false);
  const [newCat, setNewCat] = useState('');

  // Reset form when modal opens/closes or when editData changes
  useEffect(() => {
    if (isOpen) {
      if (editData) {
        // Ensure category exists in list
        const catList = [...categories];
        if (editData.category && !catList.includes(editData.category)) {
          catList.push(editData.category);
          setCategories(catList);
          localStorage.setItem('merchant_product_categories', JSON.stringify(catList));
        }

        setFormData({
          name: editData.name || '',
          price: editData.price || '',
          category: editData.category || catList[0] || 'Flowers',
          description: editData.description || '',
          image_url: editData.image_url || '',
          stock_count: editData.stock_count || 0,
          low_stock_threshold: editData.low_stock_threshold || 5,
          track_inventory: editData.track_inventory === 1,
          fulfillment_status: editData.fulfillment_status || 'pending',
        });
        setCreatedId(editData.id);
        setStep(0);

        // Fetch variations for this product
        const fetchVariations = async () => {
          try {
            const res = await writeFetch(`read/${VARIATIONS_TABLE}?product_id=${editData.id}`);
            const data = await res.json();
            if (data.status === 'success' && data.data) {
              setVariations(data.data.map(v => ({
                ...v,
                _key: crypto.randomUUID()
              })));
            }
          } catch (err) {
            console.error('Failed to fetch variations:', err);
          }
        };
        fetchVariations();

      } else {
        resetForm();
      }
    }
  }, [editData, isOpen, categories]);

  const set = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));

  const addVariation    = () => setVariations(v => [...v, emptyVariation()]);
  const removeVariation = (key) => setVariations(v => v.filter(x => x._key !== key));
  const updateVariation = (key, field, value) =>
    setVariations(v => v.map(x => x._key === key ? { ...x, [field]: value } : x));

  const resetForm = () => {
    setStep(0); setVariations([]); setCreatedId(null);
    setFormData({
      name: '', price: '', category: categories[0] || 'Flowers', description: '',
      image_url: 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?q=80&w=1287&auto=format&fit=crop',
      stock_count: 0, low_stock_threshold: 5, track_inventory: true, fulfillment_status: 'pending',
    });
  };

  const handleSaveCategories = (newCats) => {
    setCategories(newCats);
    localStorage.setItem('merchant_product_categories', JSON.stringify(newCats));
    if (!newCats.includes(formData.category) && newCats.length > 0) {
      set('category', newCats[0]);
    }
  };

  const handleAddCategory = () => {
    if (!newCat.trim()) return;
    if (categories.includes(newCat.trim())) return;
    handleSaveCategories([...categories, newCat.trim()]);
    setNewCat('');
  };

  const handleDeleteCategory = (cat) => {
    handleSaveCategories(categories.filter(c => c !== cat));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const user = getSession();
      const { s3Url } = await secureUpload({
        file, ownerId: user?.id || 'anonymous', category: 'product-images'
      });
      set('image_url', s3Url);
    } catch (err) {}
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleNext = async () => {
    if (step === 0 && !createdId) {
      setIsSubmitting(true);
      try {
        const user = getSession();
        const productId = crypto.randomUUID();
        const now = new Date().toISOString();
        const autoApprove = localStorage.getItem('admin_auto_approve_products') === 'true';
        const status = autoApprove ? 'approved' : 'pending';
        
        const payload = {
          id: productId, owner_id: user?.id || 'anonymous',
          name: formData.name, description: formData.description,
          price: parseFloat(formData.price) || 0, category: formData.category,
          image_url: formData.image_url,
          stock_count: parseInt(formData.stock_count, 10) || 0,
          low_stock_threshold: parseInt(formData.low_stock_threshold, 10) || 5,
          track_inventory: formData.track_inventory ? 1 : 0,
          fulfillment_status: formData.fulfillment_status,
          approval_status: status,
          is_active: 1, created_at: now, updated_at: now,
        };

        await writeFetch(`create/${PRODUCTS_TABLE}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        
        const local = JSON.parse(localStorage.getItem(`mock_products_${user?.id || 'anonymous'}`) || '[]');
        localStorage.setItem(`mock_products_${user?.id || 'anonymous'}`, JSON.stringify([payload, ...local]));
        
        setCreatedId(productId);
      } catch (e) { console.error(e); }
      setIsSubmitting(false);
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const user = getSession();
      const now = new Date().toISOString();
      const productId = createdId;
      const autoApprove = localStorage.getItem('admin_auto_approve_products') === 'true';
      const status = autoApprove ? 'approved' : 'pending';

      const updatePayload = editData ? {
        name: formData.name, description: formData.description,
        price: parseFloat(formData.price) || 0, category: formData.category,
        image_url: formData.image_url,
        fulfillment_status: formData.fulfillment_status,
        track_inventory:    formData.track_inventory ? 1 : 0,
        stock_count:        parseInt(formData.stock_count, 10) || 0,
        low_stock_threshold:parseInt(formData.low_stock_threshold, 10) || 5,
        approval_status:    status,
        updated_at:         now,
      } : {
        fulfillment_status: formData.fulfillment_status,
        track_inventory:    formData.track_inventory ? 1 : 0,
        stock_count:        parseInt(formData.stock_count, 10) || 0,
        low_stock_threshold:parseInt(formData.low_stock_threshold, 10) || 5,
        approval_status:    status,
        updated_at:         now,
      };

      await writeFetch(`update/${PRODUCTS_TABLE}/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(updatePayload),
      });

      // Clear old variations before recreating them to prevent duplicates
      if (editData) {
        try {
          const res = await writeFetch(`read/${VARIATIONS_TABLE}?product_id=${productId}`);
          const data = await res.json();
          if (data.status === 'success' && data.data) {
            for (const oldVar of data.data) {
              await writeFetch(`delete/${VARIATIONS_TABLE}/${oldVar.id}`, { method: 'DELETE' });
            }
          }
        } catch (err) { console.error('Failed to delete old variations', err); }
      }

      // Save variations
      for (const v of variations) {
        if (!v.value.trim()) continue;
        await writeFetch(`create/${VARIATIONS_TABLE}`, {
          method: 'POST',
          body: JSON.stringify({
            id: crypto.randomUUID(),
            product_id: productId, owner_id: user?.id || 'anonymous',
            name: v.type, value: v.value.trim(), type: v.type,
            price_modifier: parseFloat(v.price_modifier) || 0,
            stock_count: parseInt(v.stock_count, 10) || 0,
            sku: v.sku?.trim() || '', is_active: 1, created_at: now, updated_at: now,
          }),
        });
      }

      const local = JSON.parse(localStorage.getItem(`mock_products_${user?.id || 'anonymous'}`) || '[]');
      const updatedLocal = local.map(p => p.id === productId ? { ...p, ...formData, approval_status: status } : p);
      localStorage.setItem(`mock_products_${user?.id || 'anonymous'}`, JSON.stringify(updatedLocal));

      onSuccess?.();
      onClose();
      resetForm();
    } catch (err) { console.error('Submit error:', err); }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  const canNext = step === 0 ? !!(formData.name && formData.price) : true;
  const totalVariationStock = variations.reduce((s, v) => s + (parseInt(v.stock_count) || 0), 0);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]">

        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-white rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={editData ? FiEdit2 : FiPlus} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">{editData ? 'Edit Product' : 'List New Product'}</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                Step {step + 1} of {STEPS.length} — {STEPS[step]}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="px-8 pt-6 flex items-center gap-2 shrink-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 text-[9px] font-black uppercase tracking-widest ${
                i === step ? 'text-accent-navy' : i < step ? 'text-green-500' : 'text-text-muted'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black ${
                  i === step ? 'bg-accent-navy text-white' : i < step ? 'bg-green-500 text-white' : 'bg-brand-light text-text-muted border border-brand-dark'
                }`}>
                  {i < step ? <SafeIcon icon={FiCheck} className="w-3 h-3" /> : i + 1}
                </div>
                <span className="hidden sm:block">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-brand-dark" />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-8 space-y-5 overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Product Name</label>
                  <input required className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                    placeholder="e.g. Celestial Crystal Urn" value={formData.name} onChange={e => set('name', e.target.value)} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Base Price ($)</label>
                    <div className="relative">
                      <SafeIcon icon={FiDollarSign} className="absolute left-5 top-1/2 -translate-y-1/2 text-accent-gold" />
                      <input type="number" required className="w-full pl-12 pr-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                        placeholder="0.00" value={formData.price} onChange={e => set('price', e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2 flex flex-col">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Category</label>
                      <button type="button" onClick={() => setIsManagingCat(!isManagingCat)} className="text-[10px] text-accent-gold hover:underline flex items-center gap-1">
                        <SafeIcon icon={FiSettings} className="w-3 h-3" />
                        {isManagingCat ? 'Done Editing' : 'Manage Categories'}
                      </button>
                    </div>
                    
                    <AnimatePresence>
                      {isManagingCat ? (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          className="bg-brand-light border-2 border-brand-dark rounded-2xl p-4 space-y-3 overflow-hidden mt-1">
                          <div className="flex gap-2">
                            <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category"
                              className="flex-1 px-4 py-2 bg-white border border-brand-dark rounded-xl outline-none text-sm focus:border-accent-gold" />
                            <button type="button" onClick={handleAddCategory} disabled={!newCat.trim()}
                              className="px-4 py-2 bg-accent-navy text-white rounded-xl text-xs font-bold hover:bg-accent-gold transition-colors disabled:opacity-50">
                              Add
                            </button>
                          </div>
                          <div className="max-h-32 overflow-y-auto space-y-2 pr-2">
                            {categories.map(cat => (
                              <div key={cat} className="flex items-center justify-between bg-white px-3 py-2 rounded-xl border border-brand-dark">
                                <span className="text-sm text-accent-navy">{cat}</span>
                                <button type="button" onClick={() => handleDeleteCategory(cat)} className="text-red-500 hover:text-red-700">
                                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      ) : (
                        <select className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm mt-1"
                          value={formData.category} onChange={e => set('category', e.target.value)}>
                          {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Description</label>
                  <textarea className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none h-28 resize-none text-sm leading-relaxed"
                    placeholder="Describe the significance and details of this item..."
                    value={formData.description} onChange={e => set('description', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
                    <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Primary Image URL
                  </label>
                  <div className="flex gap-2">
                    <input className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm flex-1"
                      placeholder="https://..." value={formData.image_url} onChange={e => set('image_url', e.target.value)} />
                    <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading}
                      className="px-6 py-4 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-colors flex items-center justify-center gap-2 shadow-md disabled:opacity-70 font-black text-[10px] uppercase tracking-widest shrink-0">
                      {isUploading ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiUpload} className="w-4 h-4" />}
                      <span className="hidden sm:inline">Upload</span>
                    </button>
                  </div>
                  <p className="text-[10px] text-text-muted mt-1">Provide a URL or upload a high-quality image of your product.</p>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="flex items-center justify-between p-5 bg-brand-light rounded-2xl border border-brand-dark">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiPackage} className="w-5 h-5 text-accent-navy" />
                    <div>
                      <p className="text-sm font-bold text-accent-navy">Track Inventory</p>
                      <p className="text-xs text-text-muted font-light">Monitor stock levels</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => set('track_inventory', !formData.track_inventory)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${formData.track_inventory ? 'bg-accent-navy' : 'bg-gray-200'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.track_inventory ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {formData.track_inventory && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                        Base Stock Count
                      </label>
                      <input type="number" min="0" className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                        value={formData.stock_count} onChange={e => set('stock_count', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
                        <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 text-amber-500" /> Low Stock Alert Threshold
                      </label>
                      <input type="number" min="1" className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                        value={formData.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)} />
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-accent-navy/5 border border-accent-navy/10 rounded-2xl">
                  <SafeIcon icon={FiLayers} className="w-5 h-5 text-accent-navy flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-accent-navy">Product Variations</p>
                    <p className="text-xs text-text-muted font-light mt-0.5">Add color, size, material or other variants with individual pricing and stock.</p>
                  </div>
                </div>
                <AnimatePresence>
                  {variations.map((v, i) => (
                    <VariationRow key={v._key} variation={v} index={i} onChange={updateVariation} onRemove={removeVariation} />
                  ))}
                </AnimatePresence>
                <button type="button" onClick={addVariation}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold hover:text-accent-navy transition-all">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Variation
                </button>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}>
                {createdId ? (
                  <ProductImageManager productId={createdId} productName={formData.name} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-text-muted gap-3">
                    <SafeIcon icon={FiImage} className="w-10 h-10" />
                    <p className="text-sm">Complete the Details step first.</p>
                  </div>
                )}
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2 mb-4">
                    <SafeIcon icon={FiTruck} className="w-3.5 h-3.5" /> Order Fulfillment Status
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {FULFILLMENT_STATUSES.map(fs => (
                      <button key={fs.value} type="button" onClick={() => set('fulfillment_status', fs.value)}
                        className={`p-4 rounded-2xl border-2 text-left transition-all ${
                          formData.fulfillment_status === fs.value ? fs.color + ' border-current' : 'bg-brand-light border-brand-dark hover:border-accent-gold'
                        }`}>
                        <p className="text-sm font-bold">{fs.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-brand-light p-5 rounded-2xl border border-brand-dark">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Final Summary</p>
                  <div className="space-y-1.5 text-xs text-text-muted">
                    <p><span className="font-bold text-accent-navy">Name:</span> {formData.name}</p>
                    <p><span className="font-bold text-accent-navy">Base Price:</span> ${formData.price}</p>
                    <p><span className="font-bold text-accent-navy">Category:</span> {formData.category}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="px-8 pb-8 flex gap-3 shrink-0 pt-4 border-t border-brand-light mt-auto">
          {step > 0 && (
            <button type="button" onClick={() => setStep(s => s - 1)}
              className="flex items-center justify-center gap-2 px-6 py-4 rounded-2xl border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-all">
              <SafeIcon icon={FiChevronLeft} /> Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={handleNext} disabled={!canNext || isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-accent-navy text-white text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md">
              {isSubmitting
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <>Continue <SafeIcon icon={FiChevronRight} /></>}
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-accent-gold text-accent-navy text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-xl">
              {isSubmitting
                ? <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                : <><SafeIcon icon={FiCheck} /> {editData ? 'Save Changes' : 'Send for Approval'}</>}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AddProductModal;